// Pack size configuration
const PACK_SIZES = {
  5:  { discountCode: '5SET',  discountAmount: 1000,  label: '5er Set'  },
  7:  { discountCode: '7SET',  discountAmount: 3000,  label: '7er Set'  },
  14: { discountCode: '14SET', discountAmount: 7000,  label: '14er Set' }
};

class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.bundleProductEls  = this.querySelectorAll('[data-bundle-builder-product]');
    this.bundleItemEls     = this.querySelectorAll('[data-bundle-builder-item]');
    this.itemCountEl       = this.querySelector('[data-bundle-builder-item-count-current]');
    this.itemCountMaxEl    = this.querySelector('[data-bundle-builder-item-count-max]');
    this.productSubmitEl   = this.querySelector('[data-product-submit]');
    this.priceEls          = this.querySelectorAll('[data-bundle-builder-price]');
    this.formEl            = this.querySelector('[data-bundle-builder-form]');
    this.form              = this.querySelector('[data-product-form]');
    this.toggleFormEl      = this.querySelector('[data-bundle-builder-form-toggle]');

    // Pack size state – default 5
    this.currentPackSize = 5;
    this.itemCountMax    = 5;

    this.sellingPlanGroupId = this.dataset.bundleBuilderSellingPlanGroup;
    this.itemCountCurrent   = 0;
    this.selectedIds        = [];

    this.subscriptionToggle = this.querySelector('[data-product-subscription-toggle-group]');
    this.subscriptionRadios = this.querySelectorAll('[data-product-subscription-toggle]');
    this.sellingPlanSelect  = this.querySelector('[data-product-selling-plans-select]');
    this.subscriptionPrices = this.querySelectorAll('[data-product-subscribe-price], [data-product-subscribe-original-price]');

    this.initFormToggle();
    this.initPackSizeSelector();
    this.initBundleBuilder();
    this.updateSlotVisibility();

    this.updateViewportHeight(true);
    window.addEventListener('resize', () => this.updateViewportHeight(false));
  }

  // ─── Pack size selector ───────────────────────────────────────────────────

  initPackSizeSelector() {
    const tabs = this.querySelectorAll('[data-pack-size]');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const newSize = parseInt(tab.getAttribute('data-pack-size'), 10);
        if (newSize === this.currentPackSize) return;
        this.setPackSize(newSize);
        tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
      });
    });
  }

  setPackSize(newSize) {
    this.currentPackSize = newSize;
    this.itemCountMax    = newSize;

    // Remove items that exceed the new max (from the end)
    const activeItems = Array.from(this.bundleItemEls)
      .filter(item => item.hasAttribute('data-active-item'));
    if (activeItems.length > newSize) {
      activeItems.slice(newSize).forEach(item => this.removeItem(item));
    }

    this.updateSlotVisibility();

    // Update the "/N" display in the title
    if (this.itemCountMaxEl) this.itemCountMaxEl.textContent = newSize;

    this.checkItemCount();
  }

  updateSlotVisibility() {
    this.bundleItemEls.forEach((item, index) => {
      item.classList.toggle('bundle-builder__item--hidden', index >= this.currentPackSize);
    });
  }

  // ─── Core bundle builder ─────────────────────────────────────────────────

  initBundleBuilder() {
    this.overrideFormButton();
    if (this.productSubmitEl) {
      this.productSubmitEl.classList.add('disabled');
      this.productSubmitEl.disabled = true;
    }

    this.bundleProductEls.forEach((product) => {
      const addButton     = product.querySelector('[data-bundle-builder-add]');
      const quantityInput = product.querySelector('[data-bundle-builder-quantity]');
      if (addButton)     addButton.addEventListener('click', (e) => { e.preventDefault(); this.addItem(product); });
      if (quantityInput) this.quantitySetup(quantityInput, product);
    });

    this.bundleItemEls.forEach((item) => {
      const removeButton = item.querySelector('[data-bundle-builder-item-remove]');
      if (removeButton) removeButton.addEventListener('click', (e) => { e.preventDefault(); this.removeItem(item); });
    });

    this.subscriptionRadios.forEach(radio => radio.addEventListener('change', () => this.updateSubscription()));

    if (this.sellingPlanSelect) {
      this.sellingPlanSelect.addEventListener('change', () => this.updateSellingPlan());
    }
  }

  initFormToggle() {
    if (!this.toggleFormEl) return;
    const formWrapper = this.querySelector('.bundle-builder__form-wrapper');
    if (!formWrapper) return;
    this.toggleFormEl.addEventListener('click', () => {
      formWrapper.classList.toggle('active');
      document.body.classList.toggle('scroll-lock-mobile');
    });
  }

  updateViewportHeight(isInitial) {
    const viewportHeight = window.innerHeight;
    document.documentElement.style.setProperty('--viewport-height', `${viewportHeight}px`);
    if (isInitial) document.documentElement.style.setProperty('--initial-viewport-height', `${viewportHeight}px`);
  }

  updateFormState() {
    const formWrapper = this.querySelector('.bundle-builder__form-wrapper');
    if (!formWrapper) return;
    const visibleItems    = Array.from(this.bundleItemEls).filter((_, i) => i < this.currentPackSize);
    const allItemsFilled  = visibleItems.every(item => item.hasAttribute('data-active-item'));
    if (allItemsFilled) {
      formWrapper.classList.add('active');
      document.body.classList.add('scroll-lock-mobile');
    } else {
      formWrapper.classList.remove('active');
      document.body.classList.remove('scroll-lock-mobile');
    }
  }

  addItem(product) {
    if (this.itemCountCurrent >= this.itemCountMax) return;

    const productData    = JSON.parse(product.querySelector('[data-bundle-builder-product-json]').innerHTML);
    const availableItem  = Array.from(this.bundleItemEls)
      .filter((_, i) => i < this.currentPackSize)
      .find(item => !item.classList.contains('active'));
    if (!availableItem) return;

    const selectedVariant =
      productData.variants.find(v =>
        v.selling_plan_allocations?.find(p => p.selling_plan_group_id === this.sellingPlanGroupId)
      ) || productData.variants[0];

    this.updateItemAttributes(availableItem, productData, selectedVariant);
    this.appendProductImage(availableItem, productData);

    product.classList.add('active');
    this.itemCountCurrent += 1;
    this.itemCountEl.innerHTML = this.itemCountCurrent;
    this.updateQuantityDisplay(product);
    this.checkItemCount();
    this.updateFormState();
  }

  updateItemAttributes(item, productData, selectedVariant) {
    item.setAttribute('data-active-item', productData.id);
    item.setAttribute('data-active-variant', selectedVariant.id);
    item.setAttribute('data-active-item-price', selectedVariant.price);
    item.setAttribute('data-active-item-compare-price', selectedVariant.compare_at_price);

    const selectedThisVariant = this.sellingPlanSelect
      ? productData.selling_plans.find(sp => sp.name.toString() === this.sellingPlanSelect.value)
      : null;
    if (selectedThisVariant) {
      const newSellingPlanId = selectedThisVariant.id || '';
      item.setAttribute('data-active-selling-plan-id', newSellingPlanId);
      const findSelectedVariantId = selectedVariant.selling_plan_allocations?.find(
        sp => sp.selling_plan_id === selectedThisVariant.id
      );
      if (findSelectedVariantId) {
        item.setAttribute('data-active-selling-plan-price', findSelectedVariantId.price);
      }
    }
    item.classList.add('active');
  }

  appendProductImage(item, productData) {
    const figure  = document.createElement('figure');
    figure.classList.add('bundle-builder__item-figure');
    const img     = document.createElement('img');
    img.src       = productData.featured_image || '';
    img.alt       = productData.title;
    img.loading   = 'lazy';
    img.width     = '350';
    img.height    = '350';
    img.classList.add('bundle-builder__item-img');
    figure.appendChild(img);
    item.prepend(figure);

    if (productData.short_title) {
      const shortTitle       = document.createElement('p');
      shortTitle.classList.add('bundle-builder__item-title');
      shortTitle.textContent = productData.short_title;
      item.appendChild(shortTitle);
    }
  }

  removeItem(item) {
    if (this.itemCountCurrent <= 0 || !item.classList.contains('active')) return;

    const figure     = item.querySelector('.bundle-builder__item-figure');
    const shortTitle = item.querySelector('.bundle-builder__item-title');
    if (figure)     figure.remove();
    if (shortTitle) shortTitle.remove();

    const productId = item.getAttribute('data-active-item');
    item.removeAttribute('data-active-item');
    item.removeAttribute('data-active-variant');
    item.removeAttribute('data-active-item-price');
    item.removeAttribute('data-active-selling-plan-price');
    item.removeAttribute('data-active-item-product-type');
    item.classList.remove('active');

    this.itemCountCurrent -= 1;
    this.itemCountEl.innerHTML = this.itemCountCurrent;
    this.checkItemCount();

    const product = Array.from(this.bundleProductEls).find(
      el => el.getAttribute('data-bundle-builder-product') === productId
    );
    if (product) this.updateQuantityDisplay(product);

    const isStillActive = Array.from(this.bundleItemEls).some(
      el => el.getAttribute('data-active-item') === productId
    );
    if (!isStillActive) {
      this.bundleProductEls.forEach(p => {
        if (p.getAttribute('data-bundle-builder-product') === productId) p.classList.remove('active');
      });
    }
  }

  quantitySetup(quantityInput, product) {
    quantityInput.querySelectorAll('[data-quantity-button]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        if (button.dataset.action === 'plus') {
          this.addItem(product);
        } else {
          const activeItems = Array.from(this.bundleItemEls).filter(
            item => item.getAttribute('data-active-item') === product.getAttribute('data-bundle-builder-product')
          );
          if (activeItems.length > 0) this.removeItem(activeItems[activeItems.length - 1]);
        }
      });
    });
  }

  updateQuantityDisplay(product) {
    const quantityInput = product.querySelector('.quantity-selector__input');
    if (quantityInput) {
      const productId = product.getAttribute('data-bundle-builder-product');
      const count     = Array.from(this.bundleItemEls).filter(
        item => item.getAttribute('data-active-item') === productId
      ).length;
      quantityInput.value = count;
    }
  }

  checkItemCount() {
    if (!this.productSubmitEl) return;
    this.updatePriceDisplay();

    if (this.itemCountCurrent >= this.itemCountMax) {
      this.bundleProductEls.forEach(product => {
        const addButton = product.querySelector('[data-bundle-builder-add]');
        product.querySelectorAll('[data-quantity-button]').forEach(btn => {
          if (btn.getAttribute('data-action') === 'plus') btn.classList.add('disabled');
        });
        if (addButton) addButton.classList.add('disabled');
      });
      this.productSubmitEl.classList.remove('disabled');
      this.productSubmitEl.disabled = false;
    } else {
      this.bundleProductEls.forEach(product => {
        const addButton = product.querySelector('[data-bundle-builder-add]');
        product.querySelectorAll('[data-quantity-button]').forEach(btn => {
          if (btn) btn.classList.remove('disabled');
        });
        if (addButton) addButton.classList.remove('disabled');
      });
      this.productSubmitEl.classList.add('disabled');
      this.productSubmitEl.disabled = true;
    }
  }

  updatePriceDisplay() {
    let totalPrice                    = 0;
    let totalComparePrice             = 0;
    let totalSubscriptionPrice        = 0;
    let totalSubscriptionComparePrice = 0;

    // Dynamic discount based on selected pack size (in cents)
    const discountAmount = PACK_SIZES[this.currentPackSize]?.discountAmount ?? 1000;

    this.bundleItemEls.forEach(item => {
      if (item.hasAttribute('data-active-item')) {
        const itemPrice                    = parseInt(item.getAttribute('data-active-item-price'), 10) || 0;
        const itemComparePrice             = parseInt(item.getAttribute('data-active-item-compare-price'), 10) || 0;
        const itemSubscriptionPrice        = parseInt(item.getAttribute('data-active-selling-plan-price'), 10) || 0;
        const itemSubscriptionComparePrice = parseInt(item.getAttribute('data-active-item-price'), 10) || 0;

        totalPrice                    += itemPrice;
        totalComparePrice             += itemComparePrice;
        totalSubscriptionPrice        += itemSubscriptionPrice;
        totalSubscriptionComparePrice += itemSubscriptionComparePrice;
      }
    });

    const priceElement          = this.formEl.querySelector('[data-product-price]');
    const comparePriceElement   = this.formEl.querySelector('[data-product-compare-price]');
    const priceWrapper          = this.formEl.querySelector('[data-product-price-wrapper]');
    const subscriptionPriceEl   = this.formEl.querySelector('[data-product-subscribe-price]');
    const subscriptionCompareEl = this.formEl.querySelector('[data-product-subscribe-original-price]');

    const finalPrice = totalPrice - discountAmount;

    if (this.itemCountCurrent === this.itemCountMax) {
      priceElement.innerHTML          = formatMoney(finalPrice);
      comparePriceElement.innerHTML   = formatMoney(totalPrice);
      subscriptionPriceEl.innerHTML   = formatMoney(totalSubscriptionPrice);
      subscriptionCompareEl.innerHTML = formatMoney(totalSubscriptionComparePrice);

      priceElement.classList.remove('hidden');
      subscriptionPriceEl.classList.remove('hidden');
      subscriptionCompareEl.classList.remove('hidden');

      const isSale = totalPrice > finalPrice;
      comparePriceElement.classList.toggle('hidden', !isSale);
      priceWrapper.classList.toggle('sale', isSale);

      const isSubscriptionSale = totalSubscriptionComparePrice > totalSubscriptionPrice;
      subscriptionCompareEl.classList.toggle('hidden', !isSubscriptionSale);
    } else {
      [priceElement, comparePriceElement, subscriptionPriceEl, subscriptionCompareEl].forEach(
        el => el.classList.add('hidden')
      );
      priceWrapper.classList.remove('sale');
    }
  }

  updateSubscription() {
    const selectedSubscription = this.formEl.querySelector('[data-product-subscription-toggle]:checked').value;
    const isSubscription       = selectedSubscription === 'subscription';

    this.bundleItemEls.forEach(item => {
      if (isSubscription) {
        const sellingPlanId = item.getAttribute('data-active-selling-plan-id');
        this.sellingPlanSelect.removeAttribute('disabled');
        if (sellingPlanId) item.setAttribute('data-selling-plan', sellingPlanId);
      } else {
        item.removeAttribute('data-selling-plan');
        this.sellingPlanSelect.setAttribute('disabled', 'disabled');
      }
    });

    this.updatePriceDisplay();
  }

  updateSellingPlan() {
    this.bundleItemEls.forEach(item => {
      const product = Array.from(this.bundleProductEls).find(p => {
        const json = p.querySelector('[data-bundle-builder-product-json]');
        return json && JSON.parse(json.innerHTML).id.toString() === item.getAttribute('data-active-item');
      });
      if (!product) return;

      const productJson     = JSON.parse(product.querySelector('[data-bundle-builder-product-json]').innerHTML);
      const selectedVariant = productJson.selling_plans.find(
        sp => sp.name.toString() === this.sellingPlanSelect.value
      );
      if (selectedVariant) {
        const newSellingPlanId = selectedVariant.id || '';
        item.setAttribute('data-active-selling-plan-id', newSellingPlanId);
        item.setAttribute('data-selling-plan', newSellingPlanId);
      }
    });
    this.checkItemCount();
  }

  applySubscriptionMode(isSubscription) {
    this.bundleItemEls.forEach(item => {
      if (!isSubscription) item.removeAttribute('data-active-selling-plan-id');
    });
    this.updatePriceDisplay();
  }

  overrideFormButton() {
    const formButton = this.form?.querySelector('[data-product-submit]');
    if (formButton) {
      formButton.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.addProductsToCart();
      });
    }
  }

  addProductsToCart() {
    const isSubscription = this.formEl.querySelector('[data-product-subscription-toggle]:checked').value === 'subscription';
    const packConfig     = PACK_SIZES[this.currentPackSize] || PACK_SIZES[5];
    const discountCode   = packConfig.discountCode;
    const discountAmount = packConfig.discountAmount;
    const formWrapper    = this.querySelector('.bundle-builder__form-wrapper');

    const bundleItems = Array.from(this.bundleItemEls)
      .filter(item => item.hasAttribute('data-active-item'))
      .map(item => {
        const itemData = {
          id: item.getAttribute('data-active-variant'),
          quantity: 1,
          properties: { bundle: discountCode },
        };
        if (isSubscription) {
          itemData.selling_plan = item.getAttribute('data-selling-plan') || '';
        }
        return itemData;
      });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: bundleItems }),
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to add items to cart');
        return response.json();
      })
      .then(() => {
        if (discountAmount > 0) window.SLIDECART_APPLY_DISCOUNT(discountCode);
        window.SLIDECART_UPDATE();
        setTimeout(() => {
          window.SLIDECART_OPEN();
          formWrapper.classList.remove('active');
          document.body.classList.remove('scroll-lock-mobile');
        }, 300);
        formWrapper.scrollTo(0, 0);
      })
      .catch(error => console.error('Error adding products to cart:', error));
  }
}

if (!window.customElements.get('bundle-builder')) {
  window.customElements.define('bundle-builder', BundleBuilder);
}
